import { CLASSIC_SHOP_URL } from '../../app/constants';
import { NAV_SCENE_IDS, type SceneId } from '../../scenes/scene.types';

const LABELS: Record<SceneId, string> = {
  hall: 'Hall',
  alimentation: 'Alimentation',
  friandises: 'Friandises',
  comptoir: 'Comptoir',
  reserve: 'Réserve',
  galerie: 'Allée',
};

export interface Navigation {
  element: HTMLElement;
  setActive(sceneId: SceneId): void;
  setCartCount(count: number): void;
}

/** Libellé lisible à voix haute : « panier vide », « 1 article », « 3 articles ». */
export function articlesLabel(count: number): string {
  if (count <= 0) return 'panier vide';
  return count === 1 ? '1 article' : `${count} articles`;
}

/** Barre discrète, toujours accessible, avec le panier et la sortie vers le site classique. */
export function createNavigation(
  onNavigate: (sceneId: SceneId) => void,
  onOpenCart: () => void,
): Navigation {
  const element = document.createElement('nav');
  element.className = 'nav';
  element.setAttribute('aria-label', 'Pièces de la boutique');

  const list = document.createElement('ul');
  list.className = 'nav__list';

  const buttons = new Map<SceneId, HTMLButtonElement>();
  for (const sceneId of NAV_SCENE_IDS) {
    const item = document.createElement('li');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'nav__button';
    button.textContent = LABELS[sceneId];
    button.addEventListener('click', () => onNavigate(sceneId));
    buttons.set(sceneId, button);
    item.append(button);
    list.append(item);
  }

  const cartItem = document.createElement('li');
  cartItem.className = 'nav__item--cart';
  const cartButton = document.createElement('button');
  cartButton.type = 'button';
  cartButton.className = 'nav__button nav__button--cart';
  cartButton.addEventListener('click', onOpenCart);

  const cartIcon = document.createElement('span');
  cartIcon.className = 'nav__cart-icon';
  cartIcon.setAttribute('aria-hidden', 'true');
  // Balisage statique, sans donnée extérieure : pas de surface d'injection.
  cartIcon.innerHTML =
    '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" focusable="false">' +
    '<path d="M3 4h2l2.3 10.2a2 2 0 0 0 2 1.6h7.5a2 2 0 0 0 1.9-1.5L21 7H6"/>' +
    '<circle cx="10" cy="20" r="1.3"/><circle cx="18" cy="20" r="1.3"/></svg>';

  const cartLabel = document.createElement('span');
  cartLabel.className = 'nav__cart-label';
  cartLabel.textContent = 'Panier';

  const cartCount = document.createElement('span');
  cartCount.className = 'nav__cart-count';
  // Le nombre est déjà dans l'aria-label du bouton : inutile de le relire deux fois.
  cartCount.setAttribute('aria-hidden', 'true');

  cartButton.append(cartIcon, cartLabel, cartCount);
  cartItem.append(cartButton);
  list.append(cartItem);

  const classicItem = document.createElement('li');
  classicItem.className = 'nav__item--classic';
  const classic = document.createElement('a');
  classic.className = 'nav__button nav__button--classic';
  classic.href = CLASSIC_SHOP_URL;
  classic.rel = 'noopener noreferrer';
  // Libellé court pour tenir sur 360 px ; l'intitulé complet reste pour les lecteurs d'écran.
  classic.setAttribute('aria-label', 'Ouvrir la boutique classique Chien Heureux');
  classic.textContent = 'Boutique ↗';
  classicItem.append(classic);
  list.append(classicItem);

  element.append(list);

  function setCartCount(count: number): void {
    const safe = Math.max(0, count);
    cartCount.textContent = String(safe);
    // L'état vide ne se lit pas qu'à la couleur : le libellé le dit.
    cartButton.dataset['filled'] = String(safe > 0);
    cartButton.setAttribute('aria-label', `Ouvrir le panier, ${articlesLabel(safe)}`);
  }

  setCartCount(0);

  /**
   * Sur téléphone, les six salles ne tiennent pas dans la barre : elle défile.
   * Deux ombres aux extrémités le signalent, sinon rien n'indique qu'il reste
   * des salles à droite.
   */
  function refreshScrollHints(): void {
    const max = list.scrollWidth - list.clientWidth;
    element.dataset['scrollStart'] = String(max > 4 && list.scrollLeft > 4);
    element.dataset['scrollEnd'] = String(max > 4 && list.scrollLeft < max - 4);
  }

  list.addEventListener('scroll', refreshScrollHints, { passive: true });
  window.addEventListener('resize', refreshScrollHints);
  requestAnimationFrame(refreshScrollHints);

  return {
    element,
    setActive(sceneId: SceneId): void {
      for (const [id, button] of buttons) {
        const active = id === sceneId;
        button.classList.toggle('is-active', active);
        if (active) {
          button.setAttribute('aria-current', 'page');
        } else {
          button.removeAttribute('aria-current');
        }
      }

      // La salle courante doit rester visible même quand la barre déborde.
      const activeButton = buttons.get(sceneId);
      if (activeButton && list.scrollWidth > list.clientWidth) {
        const target =
          activeButton.offsetLeft - (list.clientWidth - activeButton.offsetWidth) / 2;
        list.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
      }
      refreshScrollHints();
    },
    setCartCount,
  };
}
